import { logError } from 'setup/logger/logError';
import type { LocalStorageKey } from './keys/LocalStorageKeyFactory';

/**
 * Storage error types
 */
export enum StorageErrorType {
  NOT_FOUND = 'not_found',
  PARSE_ERROR = 'parse_error',
}

/**
 * Result type for localStorage operations
 * Allows distinguishing between "not found" and "parse error"
 */
export type StorageResult<T> =
  | { success: true; value: T }
  | {
      success: false;
      error: StorageErrorType;
      details?: unknown;
    };

/**
 * Helper to create success result
 */
function success<T>(value: T): StorageResult<T> {
  return { success: true, value };
}

/**
 * Helper to create error result
 */
function failure<T>(
  error: StorageErrorType,
  details?: unknown
): StorageResult<T> {
  return { success: false, error, details };
}

/**
 * Creates a memory storage implementation for SSR/tests
 */
function createMemoryStorage(): Storage {
  const store: Record<string, string> = {};
  return {
    getItem: (key: string) => store[key] ?? null,
    setItem: (key: string, value: string) => {
      store[key] = value;
    },
    removeItem: (key: string) => {
      delete store[key];
    },
    clear: () => {
      Object.keys(store).forEach((k) => delete store[k]);
    },
    key: (index: number) => Object.keys(store)[index] ?? null,
    get length() {
      return Object.keys(store).length;
    },
  };
}

/**
 * Facade class for localStorage operations with type safety and automatic JSON serialization
 *
 * Provides a typesafe interface to localStorage that:
 * - Requires branded LocalStorageKey types (prevents typos)
 * - Automatically serializes/deserializes JSON
 * - Includes error handling
 * - Supports user-scoped keys
 * - Mockable for tests via storage provider
 */
export class LocalStorage {
  // Storage provider - allows mocking for tests
  private static storage: Storage =
    typeof window !== 'undefined'
      ? window.localStorage
      : createMemoryStorage();

  /**
   * Sets the storage provider (useful for testing)
   * @param storage - Custom Storage implementation
   */
  static setStorage(storage: Storage): void {
    this.storage = storage;
  }

  /**
   * Resets to default window.localStorage
   */
  static resetStorage(): void {
    this.storage =
      typeof window !== 'undefined'
        ? window.localStorage
        : createMemoryStorage();
  }

  /**
   * Estimates current localStorage size in KB
   * @returns Approximate size in kilobytes
   */
  static getStorageSize(): number {
    let total = 0;
    const length = this.storage.length;
    
    for (let i = 0; i < length; i++) {
      const key = this.storage.key(i);
      if (key) {
        const value = this.storage.getItem(key);
        total += key.length + (value?.length || 0);
      }
    }
    return Math.ceil(total / 1024);
  }

  /**
   * Gets detailed information about localStorage usage
   * 
   * @param thresholdPercent - Percentage threshold for warning (default 90%)
   * @returns Object with storage usage information
   * 
   * @example
   * const info = LocalStorage.getStorageInfo();
   * if (info.isNearLimit) {
   *   console.warn(`Storage ${info.percentUsed}% full (${info.sizeKB}KB)`);
   * }
   */
  static getStorageInfo(thresholdPercent = 90): {
    sizeKB: number;
    estimatedLimitKB: number;
    isNearLimit: boolean;
    percentUsed: number;
  } {
    const sizeKB = this.getStorageSize();
    // Most browsers limit localStorage to ~5-10MB
    // Using 5MB as conservative estimate
    const estimatedLimitKB = 5120;
    const percentUsed = Math.min(
      Math.round((sizeKB / estimatedLimitKB) * 100),
      100
    );
    
    return {
      sizeKB,
      estimatedLimitKB,
      isNearLimit: percentUsed >= thresholdPercent,
      percentUsed,
    };
  }

  /**
   * Saves a value to localStorage
   * @param key - Branded LocalStorageKey
   * @param value - Value to save (will be automatically serialized to JSON)
   *
   * @example
   * LocalStorage.set(USER_TOKEN, 'abc123');
   * LocalStorage.set(USER_PREFERENCES, { theme: 'dark', language: 'pl' });
   */
  static set<T>(key: LocalStorageKey, value: T): void {
    let serialized: string;
    try {
      serialized = JSON.stringify(value);
    } catch (error) {
      if (
        error instanceof TypeError &&
        error.message.includes('circular')
      ) {
        logError(
          `Cannot serialize circular structure for key: ${key}`,
          error
        );
      } else {
        logError(`Failed to serialize value for key: ${key}`, error);
      }
      throw error;
    }

    try {
      this.storage.setItem(key, serialized);
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'QuotaExceededError'
      ) {
        const currentSize = this.getStorageSize();
        const attemptedSize = Math.ceil(serialized.length / 1024);
        logError(
          `localStorage quota exceeded when setting key: ${key}. ` +
            `Current size: ~${currentSize}KB, ` +
            `Attempted to add: ~${attemptedSize}KB`,
          error
        );
      } else {
        logError(`Failed to set localStorage item for key: ${key}`, error);
      }
      throw error;
    }
  }

  /**
   * Retrieves a value from localStorage
   *
   * @param key - Branded LocalStorageKey
   * @param defaultValue - Optional default value to return if key doesn't exist or parsing fails
   * @returns StorageResult<T> if no default provided, T if default provided
   *
   * @example
   * // Without default - returns Result
   * const result = LocalStorage.get<boolean>(MY_FLAG);
   * if (result.success) {
   *   console.log(result.value);
   * } else if (result.error === StorageErrorType.PARSE_ERROR) {
   *   // Handle corrupted data
   * }
   *
   * @example
   * // With default - returns T directly
   * const archived = LocalStorage.get<boolean>(ARCHIVED_KEY, false);
   */
  static get<T>(key: LocalStorageKey): StorageResult<T>;
  static get<T>(key: LocalStorageKey, defaultValue: T): T;
  static get<T>(
    key: LocalStorageKey,
    defaultValue?: T
  ): StorageResult<T> | T {
    try {
      const item = this.storage.getItem(key);

      if (item === null) {
        // Key doesn't exist
        if (defaultValue !== undefined) {
          return defaultValue;
        }
        return failure<T>(StorageErrorType.NOT_FOUND);
      }

      const parsed = JSON.parse(item) as T;

      if (defaultValue !== undefined) {
        return parsed;
      }
      return success(parsed);
    } catch (error) {
      logError(`Failed to parse localStorage item for key: ${key}`, error);
      localStorage.removeItem(key);

      if (defaultValue !== undefined) {
        return defaultValue;
      }
      return failure<T>(StorageErrorType.PARSE_ERROR, error);
    }
  }

  /**
   * Gets a value from localStorage silently WITHOUT logging errors.
   * Use this in logging utilities to prevent infinite recursion:
   * logError → provideLogInfo → LocalStorage.get → logError → ...
   *
   * @param key - Branded LocalStorageKey (import from keys/*.ts, not from this file)
   * @param defaultValue - Value to return if key doesn't exist or parsing fails
   * @returns The parsed value or defaultValue
   *
   * @example
   * // In provideLogInfo.ts:
   * import { USER_EMAIL } from 'services/storage/keys/auth';
   * const email = LocalStorage.getSilent(USER_EMAIL, '');
   */
  static getSilent<T>(key: LocalStorageKey, defaultValue: T): T {
    try {
      const item = this.storage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch {
      // Silently return default - no logging to prevent recursion
      return defaultValue;
    }
  }

  /**
   * Removes a value from localStorage
   * @param key - Branded LocalStorageKey
   *
   * @example
   * LocalStorage.delete(USER_TOKEN);
   */
  static delete(key: LocalStorageKey): void {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      logError(
        `Failed to delete localStorage item for key: ${key}`,
        error
      );
      throw error;
    }
  }

  /**
   * Checks if a key exists in localStorage
   *
   * Note: This method only checks existence, not validity of stored data.
   * If you need the value, use get() directly instead of calling has() first
   * to avoid double reads.
   *
   * @param key - Branded LocalStorageKey
   * @returns true if key exists, false otherwise
   *
   * @example
   * // Preferred: direct get()
   * const result = LocalStorage.get<string>(TOKEN);
   * if (result.success) { ... }
   *
   * // Or with default:
   * const token = LocalStorage.get<string>(TOKEN, '');
   *
   * // has() is useful for existence checks without parsing:
   * if (LocalStorage.has(SOME_FLAG)) {
   *   // Key exists, but we don't need the value yet
   * }
   */
  static has(key: LocalStorageKey): boolean {
    return this.storage.getItem(key) !== null;
  }

  /**
   * Clears all localStorage
   *
   * @example
   * LocalStorage.clear();
   */
  static clear(): void {
    try {
      this.storage.clear();
    } catch (error) {
      logError('Failed to clear localStorage', error);
      throw error;
    }
  }

  /**
   * Get all keys from localStorage, optionally filtered by predicate
   *
   * @param predicate - Optional filter function
   * @returns Array of keys as strings (not branded)
   *
   * @example
   * // Get all keys
   * const allKeys = LocalStorage.getKeys();
   *
   * // Get keys matching a pattern
   * const userKeys = LocalStorage.getKeys(key => key.startsWith('user_'));
   */
  static getKeys(predicate?: (key: string) => boolean): string[] {
    const keys: string[] = [];

    try {
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i);
        if (key && (!predicate || predicate(key))) {
          keys.push(key);
        }
      }
    } catch (error) {
      logError('Failed to get localStorage keys', error);
    }

    return keys;
  }

  /**
   * Clear all keys matching a predicate
   *
   * @param predicate - Filter function to determine which keys to clear
   *
   * @example
   * // Clear all user-scoped keys
   * LocalStorage.clearMatching(key => key.startsWith('user_'));
   *
   * // Clear temporary keys
   * LocalStorage.clearMatching(key => key.includes('_temp_'));
   */
  static clearMatching(predicate: (key: string) => boolean): void {
    try {
      const keys = this.getKeys(predicate);
      keys.forEach((key) => {
        // Cast is safe here - we're deleting keys we just retrieved
        this.delete(key as LocalStorageKey);
      });
    } catch (error) {
      logError('Failed to clear matching localStorage keys', error);
    }
  }

}

export * from './keys';
